import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { IRootState } from '../../store';
import { toggleRTL, toggleTheme, toggleSidebar } from '../../store/themeConfigSlice';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import Dropdown from '../Dropdown';
import IconMenu from '../Icon/IconMenu';
import IconCalendar from '../Icon/IconCalendar';
import IconXCircle from '../Icon/IconXCircle';
import IconSun from '../Icon/IconSun';
import IconMoon from '../Icon/IconMoon';
import IconLaptop from '../Icon/IconLaptop';
import IconInfoCircle from '../Icon/IconInfoCircle';
import IconBellBing from '../Icon/IconBellBing';
import IconUser from '../Icon/IconUser';
import IconLockDots from '../Icon/IconLockDots';
import IconLogout from '../Icon/IconLogout';
import IconGlobe from '../Icon/IconGlobe';
import { useUserContext } from '../../contexts/UserContext';
import { capitalize } from 'lodash';
import UploadReportButton from '../UploadReportButton';
import { updateUserLanguage, updateUserSettings } from '../../services/usersServices';
import { selectSettings } from '../../store/authSlice';
import { Plan } from '../../global/types';
import IconCrown from '@/components/Icon/IconCrown';
import { LANGUAGE_OPTIONS } from '../../global/languages';

const Header = () => {
    const location = useLocation();
    useEffect(() => {
        const selector = document.querySelector('ul.horizontal-menu a[href="' + window.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
            const all: any = document.querySelectorAll('ul.horizontal-menu .nav-link.active');
            for (let i = 0; i < all.length; i++) {
                all[0]?.classList.remove('active');
            }
            const ul: any = selector.closest('ul.sub-menu');
            if (ul) {
                let ele: any = ul.closest('li.menu').querySelectorAll('.nav-link');
                if (ele) {
                    ele = ele[0];
                    setTimeout(() => {
                        ele?.classList.add('active');
                    });
                }
            }
        }
    }, [location]);

    // Auto-hide the header on downward scroll (mobile only) to reclaim vertical space; reappears on scroll up or near the top.
    const [isHeaderHidden, setIsHeaderHidden] = useState(false);
    const lastScrollY = useRef(0);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            if (window.innerWidth >= 1024 || currentScrollY < 80) {
                setIsHeaderHidden(false);
            } else if (currentScrollY > lastScrollY.current + 10) {
                setIsHeaderHidden(true);
            } else if (currentScrollY < lastScrollY.current - 10) {
                setIsHeaderHidden(false);
            }

            lastScrollY.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const isRtl = useSelector((state: IRootState) => state.themeConfig.rtlClass) === 'rtl' ? true : false;

    const themeConfig = useSelector((state: IRootState) => state.themeConfig);
    const dispatch = useDispatch();
    const settings = useSelector(selectSettings);
    const preferences = settings?.preferences;

    const hideSidebarByDefault = preferences && preferences?.hideSidebarByDefault !== undefined ? preferences.hideSidebarByDefault : false;

    const handlePreferenceChange = (key: string, value: boolean | string) => {
        if (!user?.id) return;

        // Update local Redux state for immediate UI feedback
        if (key === 'defaultTheme') dispatch(toggleTheme(value));
        if (key === 'hideSidebarByDefault') dispatch(toggleSidebar(!themeConfig.sidebar));

        // Update DB
        if (preferences) {
            updateUserSettings(user.id, {
                preferences: {
                    ...preferences,
                    [key]: value
                }
            });
        }
    };

    const { user, subscription, logout } = useUserContext();


    const [messages, setMessages] = useState<{ id: number }[]>([]);

    const removeMessage = (value: number) => {
        setMessages(messages.filter((user) => user.id !== value));
    };

    const [notifications, setNotifications] = useState<{
        id: number;
        profile: string;
        message: string;
        time: string;
    }[]>([]);

    const removeNotification = (value: number) => {
        setNotifications(notifications.filter((user) => user.id !== value));
    };

    const setLocale = (flag: string) => {
        setFlag(flag);
        if (flag.toLowerCase() === 'ae') {
            dispatch(toggleRTL('rtl'));
        } else {
            dispatch(toggleRTL('ltr'));
        }
    };
    const [flag, setFlag] = useState(themeConfig.locale);

    const { t } = useTranslation();

    return (
        <header
            className={`z-40 transition-transform duration-300 motion-reduce:transition-none ${isHeaderHidden && false ? '-translate-y-full' : 'translate-y-0'} ${themeConfig.semidark && themeConfig.menu === 'horizontal' ? 'dark' : ''
                }`}
        >
            <div className="shadow-sm">
                <div className="relative bg-white dark:bg-[#0e1726] flex w-full items-center px-5 py-2.5 border-b border-[#e0e6ed] dark:border-[#1b2e4b]">
                    <div className="horizontal-logo flex lg:hidden justify-between items-center ltr:mr-2 rtl:ml-2">
                        <Link to="/" className="main-logo flex items-center shrink-0">
                            <img className="w-11 ltr:-ml-1 rtl:-mr-1 inline md:hidden dark:hidden dark:md:hidden" src="/assets/images/logo-green.svg" alt="logo" />
                            <img className="w-11 ltr:-ml-1 rtl:-mr-1 hidden dark:inline dark:md:hidden" src="/assets/images/logo-coral.svg" alt="logo" />
                            <img src="/assets/images/logo-combination-green.png" alt="logo" className='max-w-36 hidden md:inline dark:hidden dark:md:hidden transition-all duration-300' />
                            <img src="/assets/images/logo-combination-coral.png" alt="logo" className='max-w-36 hidden dark:hidden dark:md:inline transition-all duration-300' />
                        </Link>
                        <button
                            type="button"
                            className="collapse-icon flex-none dark:text-[#d0d2d6] hover:text-primary dark:hover:text-primary flex lg:hidden ltr:ml-2 rtl:mr-2 p-3 rounded-full bg-white-light/40 dark:bg-dark/40 hover:bg-white-light/90 dark:hover:bg-dark/60"
                            onClick={() => {
                                dispatch(toggleSidebar(undefined));
                            }}
                        >
                            <IconMenu className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="ltr:mr-2 rtl:ml-2 hidden sm:hidden">
                        <ul className="flex items-center space-x-2 rtl:space-x-reverse dark:text-[#d0d2d6]">
                            <li>
                                <Link to="/dashboard/calendar" className="block p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60">
                                    <IconCalendar />
                                </Link>
                            </li>
                        </ul>
                    </div>
                    <div className="sm:flex-1 ltr:sm:ml-0 ltr:ml-auto sm:rtl:mr-0 rtl:mr-auto flex justify-end items-center space-x-1.5 lg:space-x-2 rtl:space-x-reverse dark:text-[#d0d2d6]">
                        <div className="flex items-center space-x-4 rtl:space-x-reverse">

                            {(!subscription?.plan || subscription.plan === Plan.FREE || subscription.isTrial) ? (
                                <Link
                                    to="/dashboard/users/subscription-plans"
                                    className="btn btn-sm btn-outline-primary hidden md:flex items-center gap-2 shadow-sm hover:shadow-md transition-all duration-300"
                                >
                                    <IconCrown className="w-4 h-4" />
                                    {t('upgrade_to_pro')}
                                </Link>
                            ) : (
                                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                                    <IconCrown className="w-3.5 h-3.5 text-primary" />
                                    <span className="text-xs font-bold text-primary">
                                        {t('sentola_pro')}
                                    </span>
                                </div>
                            )}

                            <UploadReportButton />
                        </div>
                        <div className="hidden sm:block">
                            {themeConfig.theme === 'light' ? (
                                <button
                                    className={`${themeConfig.theme === 'light' &&
                                        'flex items-center p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60'
                                        }`}
                                    onClick={() => {
                                        dispatch(toggleTheme('dark'));
                                    }}
                                >
                                    <IconSun />
                                </button>
                            ) : (
                                ''
                            )}
                            {themeConfig.theme === 'dark' && (
                                <button
                                    className={`${themeConfig.theme === 'dark' &&
                                        'flex items-center p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60'
                                        }`}
                                    onClick={() => {
                                        dispatch(toggleTheme('system'));
                                    }}
                                >
                                    <IconMoon />
                                </button>
                            )}
                            {themeConfig.theme === 'system' && (
                                <button
                                    className={`${themeConfig.theme === 'system' &&
                                        'flex items-center p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60'
                                        }`}
                                    onClick={() => {
                                        dispatch(toggleTheme('light'));
                                    }}
                                >
                                    <IconLaptop />
                                </button>
                            )}
                        </div>
                        <div className="dropdown shrink-0 hidden sm:block">
                            <Dropdown
                                offset={[0, 8]}
                                placement={`${isRtl ? 'bottom-start' : 'bottom-end'}`}
                                btnClassName="block p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60"
                                button={<img className="w-5 h-5 object-cover rounded-full" src={`/assets/images/flags/${flag.toUpperCase()}.svg`} alt="flag" />}
                            >
                                <ul className="!px-2 text-dark dark:text-white-dark grid grid-cols-2 gap-2 font-semibold dark:text-white-light/90 w-[280px]">
                                    {LANGUAGE_OPTIONS.map((item) => {
                                        return (
                                            <li key={item.code}>
                                                <button
                                                    type="button"
                                                    className={`flex w-full hover:text-primary dark:hover:text-primary-light rounded-lg ${i18next.language === item.code ? 'bg-primary/10 text-primary dark:bg-primary-light/10 dark:text-primary-light' : ''}`}
                                                    onClick={async () => {
                                                        i18next.changeLanguage(item.code);
                                                        updateUserLanguage(user?.id, item.code);
                                                        // setFlag(item.code);
                                                        setLocale(item.code);
                                                    }}
                                                >
                                                    <img src={`/assets/images/flags/${item.code.toUpperCase()}.svg`} alt="flag" className="w-5 h-5 object-cover rounded-full" />
                                                    <span className="ltr:ml-3 rtl:mr-3">{item.name}</span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </Dropdown>
                        </div>
                        <div className="dropdown shrink-0 hidden">
                            <Dropdown
                                disabled
                                offset={[0, 8]}
                                placement={`${isRtl ? 'bottom-start' : 'bottom-end'}`}
                                btnClassName="relative block p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60"
                                button={
                                    <span>
                                        <IconBellBing />
                                        <span className="flex absolute w-3 h-3 ltr:right-0 rtl:left-0 top-0">
                                            <span className="animate-ping absolute ltr:-left-[3px] rtl:-right-[3px] -top-[3px] inline-flex h-full w-full rounded-full bg-success/50 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full w-[6px] h-[6px] bg-success"></span>
                                        </span>
                                    </span>
                                }
                            >
                                <ul className="!py-0 text-dark dark:text-white-dark w-[300px] sm:w-[350px] divide-y dark:divide-white/10">
                                    <li onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center px-4 py-2 justify-between font-semibold">
                                            <h4 className="text-lg">{t('notification')}</h4>
                                            {notifications.length ? <span className="badge bg-primary/80">{notifications.length}{t('new')}</span> : ''}
                                        </div>
                                    </li>
                                    {notifications.length > 0 ? (
                                        <>
                                            {notifications.map((notification) => {
                                                return (
                                                    <li key={notification.id} className="dark:text-white-light/90" onClick={(e) => e.stopPropagation()}>
                                                        <div className="group flex items-center px-4 py-2">
                                                            <div className="grid place-content-center rounded">
                                                                <div className="w-12 h-12 relative">
                                                                    <img className="w-12 h-12 rounded-full object-cover" alt="profile" src={`/assets/images/${notification.profile}`} />
                                                                    <span className="bg-success w-2 h-2 rounded-full block absolute right-[6px] bottom-0"></span>
                                                                </div>
                                                            </div>
                                                            <div className="ltr:pl-3 rtl:pr-3 flex flex-auto">
                                                                <div className="ltr:pr-3 rtl:pl-3">
                                                                    <h6
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: notification.message,
                                                                        }}
                                                                    ></h6>
                                                                    <span className="text-xs block font-normal dark:text-gray-500">{notification.time}</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="ltr:ml-auto rtl:mr-auto text-neutral-300 hover:text-danger opacity-0 group-hover:opacity-100"
                                                                    onClick={() => removeNotification(notification.id)}
                                                                >
                                                                    <IconXCircle />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                            <li>
                                                <div className="p-4">
                                                    <button className="btn btn-primary block w-full btn-small">{t('read_all_notifications')}</button>
                                                </div>
                                            </li>
                                        </>
                                    ) : (
                                        <li onClick={(e) => e.stopPropagation()}>
                                            <button type="button" className="!grid place-content-center hover:!bg-transparent text-lg min-h-[200px]">
                                                <div className="mx-auto ring-4 ring-primary/30 rounded-full mb-4 text-primary">
                                                    <IconInfoCircle fill={true} className="w-10 h-10" />
                                                </div>
                                                {t('no_data_available')}
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            </Dropdown>
                        </div>
                        <div className="dropdown shrink-0 flex">
                            <Dropdown
                                offset={[0, 8]}
                                placement={`${isRtl ? 'bottom-start' : 'bottom-end'}`}
                                btnClassName="relative group block"
                                button={<img className="w-9 h-9 rounded-full object-cover saturate-50 group-hover:saturate-100" src={user?.photoURL} alt="userProfile" />}
                            >
                                <ul className="text-dark dark:text-white-dark !py-0 w-[230px] font-semibold dark:text-white-light/90">
                                    <li>
                                        <div className="flex items-center px-4 py-4">
                                            <img className="rounded-md w-10 h-10 object-cover" src={user?.photoURL} alt="userProfile" />
                                            <div className="ltr:pl-4 rtl:pr-4 flex flex-col overflow-hidden">
                                                <h4 className="text-base flex items-center">
                                                    <span className="truncate">{user?.fullName || '-'}</span>
                                                    <span className="flex-none text-xs bg-success-light rounded text-success px-1 ltr:ml-2 rtl:ml-2">{capitalize(subscription?.plan || t('free'))}</span>
                                                </h4>
                                                <button type="button" className="text-black/60 hover:text-primary dark:text-dark-light/60 dark:hover:text-white truncate text-left">
                                                    {user?.email}
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <Link to="/dashboard/users/account-settings?tab=general" className="dark:hover:text-white">
                                            <IconUser className="w-4.5 h-4.5 ltr:mr-2 rtl:ml-2 shrink-0" />
                                            {t('profile')}
                                        </Link>
                                    </li>
                                    <li className="px-4 py-2 border-t border-white-light dark:border-white-light/10">
                                        <div className="flex items-center justify-between">
                                            <span className="flex items-center text-black dark:text-white-light/90 font-semibold">
                                                {themeConfig.theme === 'light' ? <IconSun className="w-4.5 h-4.5 ltr:mr-2 rtl:ml-2 shrink-0" /> : <IconMoon className="w-4.5 h-4.5 ltr:mr-2 rtl:ml-2 shrink-0" />}
                                                {t('theme')}
                                            </span>
                                            <label className="w-8 h-4 relative cursor-pointer mb-0">
                                                <input
                                                    type="checkbox"
                                                    className="custom_switch absolute w-full h-full opacity-0 z-10 cursor-pointer peer"
                                                    checked={themeConfig.theme === 'dark'}
                                                    onChange={() => handlePreferenceChange('defaultTheme', themeConfig.theme === 'light' ? 'dark' : 'light')}
                                                />
                                                <span className="bg-[#ebedf2] dark:bg-dark block h-full rounded-full before:absolute before:left-1 before:bg-white dark:before:bg-white-dark dark:peer-checked:before:bg-white before:bottom-1 before:w-2 before:h-2 before:rounded-full peer-checked:before:left-5 peer-checked:bg-primary before:transition-all before:duration-300"></span>
                                            </label>
                                        </div>
                                    </li>
                                    <li className="px-4 py-2 sm:hidden flex justify-between items-center">
                                        <span className="flex items-center text-black dark:text-white-light/90 font-semibold mb-2">
                                            <IconGlobe className="w-4.5 h-4.5 ltr:mr-2 rtl:ml-2 shrink-0" />
                                            {t('language', { defaultValue: 'Language' })}
                                        </span>
                                        <div className="flex flex-wrap gap-1.5">
                                            {LANGUAGE_OPTIONS.map((item) => (
                                                <button
                                                    key={item.code}
                                                    type="button"
                                                    title={item.name}
                                                    className={`p-px rounded-full border-2 transition-colors ${i18next.language === item.code ? 'border-primary' : 'border-transparent hover:border-primary/30'
                                                        }`}
                                                    onClick={async () => {
                                                        i18next.changeLanguage(item.code);
                                                        updateUserLanguage(user?.id, item.code);
                                                        setLocale(item.code);
                                                    }}
                                                >
                                                    <img src={`/assets/images/flags/${item.code.toUpperCase()}.svg`} alt={item.name} className="w-4 h-4 object-cover rounded-full" />
                                                </button>
                                            ))}
                                        </div>
                                    </li>
                                    <li className="px-4 py-2">
                                        <div className="flex items-center justify-between">
                                            <span className="flex items-center text-black dark:text-white-light/90 font-semibold">
                                                <IconLaptop className="w-4.5 h-4.5 ltr:mr-2 rtl:ml-2 shrink-0" />
                                                {t('sidebar')}
                                            </span>
                                            <label className="w-8 h-4 relative cursor-pointer mb-0">
                                                <input
                                                    type="checkbox"
                                                    className="custom_switch absolute w-full h-full opacity-0 z-10 cursor-pointer peer"
                                                    checked={!themeConfig.sidebar}
                                                    onChange={() => handlePreferenceChange('hideSidebarByDefault', themeConfig.sidebar)}
                                                />
                                                <span className="bg-[#ebedf2] dark:bg-dark block h-full rounded-full before:absolute before:left-1 before:bg-white dark:before:bg-white-dark dark:peer-checked:before:bg-white before:bottom-1 before:w-2 before:h-2 before:rounded-full peer-checked:before:left-5 peer-checked:bg-primary before:transition-all before:duration-300"></span>
                                            </label>
                                        </div>
                                    </li>
                                    <li className="border-t border-white-light dark:border-white-light/10">
                                        <button className="text-danger !py-3" onClick={logout}>
                                            <IconLogout className="w-4.5 h-4.5 ltr:mr-2 rtl:ml-2 rotate-90 shrink-0" />
                                            {t('sign_out')}
                                        </button>
                                    </li>
                                </ul>
                            </Dropdown>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
